import { ApiError, getApiBase, getToken } from "../../api";
import { formatEpoch, ownValue, safeMessage, tidy } from "../shared/safe";

// Pure parsing and wording for the per-client Media screen (client-media routes mounted by lane C; real examples in
// tests/contract/fixtures/media-dto.json). Everything from the server is untrusted text. UPLOADING IS NOT APPROVAL: an image is "uploaded" until
// someone approves that exact revision, and only an approved revision can be pinned into a site.

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const nonEmpty = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const int = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null);

/** The server's limits (docs/build/handoffs/worker-c-media-mount.md): 10 MiB decoded. Accepted types are decided by the file's bytes, not its name. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export const LIMITS_TEXT = "PNG, JPEG or WebP, up to 10 MiB, at most 20000 px per side. SVG and every other type is refused.";

export const RIGHTS_OPTIONS = ["client_owned", "licensed"] as const;
export const RIGHTS_LABELS: Record<string, string> = { client_owned: "The client owns it (supplied by the owner)", licensed: "Licensed (stock or third party)" };

export type ApprovalState = "uploaded" | "approved" | "withdrawn" | "unknown";
export const APPROVAL_LABELS: Record<ApprovalState, { label: string; tone: "ok" | "warn" | "bad" | "neutral" }> = {
  uploaded: { label: "Uploaded, NOT approved", tone: "warn" },
  approved: { label: "Approved", tone: "ok" },
  withdrawn: { label: "Approval withdrawn", tone: "bad" },
  unknown: { label: "Approval state unknown", tone: "neutral" },
};

export interface MediaRevision {
  mediaId: string;
  revisionId: string;
  revision: number;
  filename: string;
  mime: string;
  byteSize: number | null;
  sha256: string;
  width: number | null;
  height: number | null;
  alt: string;
  decorative: boolean;
  rights: string;
  rightsNote: string | null;
  generation: {model:string;prompt:string;revisedPrompt:string|null;actor:string} | null;
  approval: { state: ApprovalState; at: { text: string; iso: string } | null; approvedBy: string | null };
  publishedPath: string | null;
  importedBy: string | null;
  createdAt: { text: string; iso: string } | null;
  /** Listings only. */
  revisionCount: number | null;
}

export function readMedia(x: unknown): MediaRevision | null {
  if (!isObject(x) || !nonEmpty(x.mediaId) || !nonEmpty(x.revisionId)) return null;
  const a = isObject(x.approval) ? x.approval : {};
  const state: ApprovalState = a.state === "uploaded" || a.state === "approved" || a.state === "withdrawn" ? a.state : "unknown";
  return {
    mediaId: x.mediaId as string,
    revisionId: x.revisionId as string,
    revision: int(x.revision) ?? 1,
    filename: nonEmpty(x.filename) ? tidy(x.filename, 200) : "(no filename)",
    mime: nonEmpty(x.mime) ? tidy(x.mime, 40) : "unknown",
    byteSize: int(x.byteSize),
    sha256: nonEmpty(x.sha256) ? tidy(x.sha256, 80) : "",
    width: int(x.width),
    height: int(x.height),
    alt: typeof x.alt === "string" ? tidy(x.alt, 600) : "",
    decorative: x.decorative === true,
    rights: nonEmpty(x.rights) ? tidy(x.rights, 40) : "unknown",
    rightsNote: nonEmpty(x.rightsNote) ? tidy(x.rightsNote, 600) : null,
    generation: isObject(x.generation) ? {model:tidy(x.generation.model,100),prompt:tidy(x.generation.prompt,4500),revisedPrompt:nonEmpty(x.generation.revised_prompt)?tidy(x.generation.revised_prompt,4500):null,actor:tidy(x.generation.actor,128)} : null,
    approval: { state, at: formatEpoch(typeof a.at === "string" ? Date.parse(a.at) / 1000 : a.at), approvedBy: nonEmpty(a.approvedBy) ? tidy(a.approvedBy, 120) : null },
    publishedPath: nonEmpty(x.publishedPath) ? tidy(x.publishedPath, 300) : null,
    importedBy: nonEmpty(x.importedBy) ? tidy(x.importedBy, 120) : null,
    createdAt: formatEpoch(typeof x.createdAt === "string" ? Date.parse(x.createdAt) / 1000 : x.createdAt),
    revisionCount: int(x.revisionCount),
  };
}

export function parseMediaList(body: unknown): { items: MediaRevision[]; next: string | null } | null {
  if (!isObject(body) || !Array.isArray(body.items)) return null;
  return { items: body.items.map(readMedia).filter((m): m is MediaRevision => m !== null), next: nonEmpty(body.next) };
}
export function parseRevisions(body: unknown): MediaRevision[] | null {
  return isObject(body) && Array.isArray(body.revisions) ? body.revisions.map(readMedia).filter((m): m is MediaRevision => m !== null) : null;
}
export function parseMediaReply(body: unknown): { media: MediaRevision; replayed: boolean } | null {
  if (!isObject(body)) return null;
  const media = readMedia(body.media);
  return media ? { media, replayed: body.replayed === true } : null;
}

export interface PinResult {
  mediaId: string;
  revisionId: string;
  path: string | null;
  checksum: string;
  byteSize: number | null;
  approvalState: ApprovalState;
  approvedBy: string | null;
}
/** POST .../pin -> { pin: { snapshotMedia, mediaRef, approval } }: the exact bytes (by checksum) that a site revision would use. */
export function parsePin(body: unknown): PinResult | null {
  if (!isObject(body) || !isObject(body.pin)) return null;
  const p = body.pin;
  const ref = isObject(p.mediaRef) ? p.mediaRef : null;
  const snap = isObject(p.snapshotMedia) ? p.snapshotMedia : null;
  const a = isObject(p.approval) ? p.approval : {};
  if (!ref || !snap || !nonEmpty(ref.mediaId) || !nonEmpty(ref.revisionId)) return null;
  return {
    mediaId: ref.mediaId as string,
    revisionId: ref.revisionId as string,
    path: nonEmpty(ref.path) ? tidy(ref.path, 300) : null,
    checksum: nonEmpty(ref.checksum) ? tidy(ref.checksum, 80) : nonEmpty(snap.checksum) ? tidy(snap.checksum, 80) : "",
    byteSize: int(ref.byteSize),
    approvalState: a.state === "uploaded" || a.state === "approved" || a.state === "withdrawn" ? a.state : "unknown",
    approvedBy: nonEmpty(a.approvedBy) ? tidy(a.approvedBy, 120) : null,
  };
}

// ---- formatting --------------------------------------------------------------------------------------------------------------------

export const sha8 = (hash: string): string => (hash ? `${hash.slice(0, 12)}…` : "—");
export function sizeText(bytes: number | null): string {
  if (bytes === null) return "size unknown";
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

// ---- the upload form ---------------------------------------------------------------------------------------------------------------

export interface UploadDraft {
  filename: string;
  size: number;
  mimeGuess: string;
  alt: string;
  decorative: boolean;
  rights: string;
  rightsNote: string;
  /** Add as a new revision of this image (mediaId), or "" for a new image. */
  extend: string;
}
export const emptyUpload = (): UploadDraft => ({ filename: "", size: 0, mimeGuess: "", alt: "", decorative: false, rights: "client_owned", rightsNote: "", extend: "" });

/** What can be said about the chosen file BEFORE sending. The server decides (it reads the bytes); this only avoids obvious waste. */
export function fileNotes(f: { name: string; size: number; type: string }): { block: string | null; warn: string | null } {
  if (f.size === 0) return { block: "The file is empty.", warn: null };
  if (f.size > MAX_IMAGE_BYTES) return { block: `The file is ${sizeText(f.size)}, larger than the 10 MiB limit. Nothing was sent.`, warn: null };
  const looksSvg = /svg/i.test(f.type) || /\.svgz?$/i.test(f.name);
  if (looksSvg) return { block: null, warn: "This looks like an SVG. CloudPush refuses SVG (it can run script when opened directly). You can still try and the server will say why." };
  if (f.type && !(ACCEPTED_TYPES as readonly string[]).includes(f.type) && !/^image\//.test(f.type)) return { block: null, warn: `The browser reports this file as "${tidy(f.type, 40)}". Only PNG, JPEG and WebP images are accepted; the server decides from the file itself.` };
  return { block: null, warn: null };
}

export function uploadProblems(d: UploadDraft): { field: string; message: string }[] {
  const out: { field: string; message: string }[] = [];
  if (d.filename === "") out.push({ field: "file", message: "Choose an image file." });
  if (!d.decorative && d.alt.trim() === "") out.push({ field: "alt", message: "Write alt text describing the image, or tick Decorative if it carries no meaning. It is never written for you." });
  if (!(RIGHTS_OPTIONS as readonly string[]).includes(d.rights)) out.push({ field: "rights", message: "Say who holds the rights." });
  return out;
}

export function uploadBody(d: UploadDraft, bytesBase64: string, idempotencyKey: string): Record<string, unknown> {
  return {
    idempotencyKey,
    filename: d.filename,
    bytesBase64,
    ...(d.decorative ? { decorative: true } : { alt: d.alt.trim() }),
    rights: d.rights,
    ...(d.rightsNote.trim() ? { rightsNote: d.rightsNote.trim() } : {}),
    ...(d.extend ? { mediaId: d.extend } : {}),
  };
}

// ---- failures ----------------------------------------------------------------------------------------------------------------------

export interface MediaProblem {
  kind: "validation" | "approval_required" | "conflict" | "notfound" | "too_large" | "unavailable" | "unauthorized" | "network" | "server" | "other";
  message: string;
  /** The server's field messages verbatim (they carry the reason: an unsafe SVG, a missing alt text...). */
  fields: { field: string; message: string }[];
  /** True when the server definitely did not act. Network failures and 5xx are not definite. */
  definite: boolean;
}

export function describeMediaProblem(e: unknown, what: string): MediaProblem {
  if (!(e instanceof ApiError)) return { kind: "network", message: `${what}: couldn't reach the server, so it is not known whether anything was saved. Check the list before trying again.`, fields: [], definite: false };
  const body = e.body ?? {};
  const fe = isObject(body.fieldErrors) ? body.fieldErrors : {};
  const fields = Object.entries(fe).flatMap(([field, msgs]) => (Array.isArray(msgs) ? msgs : [msgs]).filter((m): m is string => typeof m === "string").map((m) => ({ field: tidy(field, 60), message: safeMessage(m, 500) })));
  const message = safeMessage(e.message, 500);
  const base = { fields, definite: true };
  if (e.code === "APPROVAL_REQUIRED") return { ...base, kind: "approval_required", message: `${what}: ${message}` };
  if (e.code === "DEPENDENCY_UNAVAILABLE" || e.status === 503) return { ...base, kind: "unavailable", message: `${what}: ${message}` };
  if (e.status === 413) return { ...base, kind: "too_large", message: `${what}: the file is too large for the server (limit 10 MiB). Nothing was stored.` };
  if (e.code === "CONFLICT" || e.status === 409) return { ...base, kind: "conflict", message: `${what}: ${message}` };
  if (e.status === 404) return { ...base, kind: "notfound", message: `${what}: not found (it may belong to another client or was removed). Reload.` };
  if (e.status === 401 || e.status === 403) return { ...base, kind: "unauthorized", message: `${what}: not allowed (${e.status}). Nothing was saved.` };
  if (e.status === 422 || e.status === 400) return { ...base, kind: "validation", message: `${what}: ${message}` };
  if (e.status >= 500) return { ...base, definite: false, kind: "server", message: `${what}: the server failed (HTTP ${e.status}), so it is not known whether anything was saved. Check the list before trying again.` };
  return { ...base, kind: "other", message: `${what}: ${message}` };
}

// ---- bytes (thumbnails) -------------------------------------------------------------------------------------------------------------

/** The verified bytes of one revision as a blob, through the bearer token (an <img> cannot send it). Null on any failure: a missing thumbnail is not an error. */
export async function fetchBytes(clientId: string, mediaId: string, revisionId: string, signal?: AbortSignal): Promise<Blob | null> {
  try {
    const token = getToken();
    const res = await fetch(`${getApiBase()}/v1/clients/${encodeURIComponent(clientId)}/media/${encodeURIComponent(mediaId)}/revisions/${encodeURIComponent(revisionId)}/bytes`, { headers: token ? { Authorization: `Bearer ${token}` } : { "X-CloudPush-Session": "1" }, credentials: "same-origin", signal });
    if (!res.ok) return null;
    const type = res.headers.get("content-type") ?? "";
    if (!(ACCEPTED_TYPES as readonly string[]).includes(type)) return null; // only ever an inert raster image
    return await res.blob();
  } catch {
    return null;
  }
}

export const approvalLabel = (s: ApprovalState) => ownValue(APPROVAL_LABELS, s) ?? APPROVAL_LABELS.unknown;
